import { useContext, useState, useMemo, useCallback, forwardRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import TextField from '@material-ui/core/TextField';
import Slide from '@material-ui/core/Slide';
import { serverContext, appContext, fetchFromServer, uploadToServer, themeContext } from './App';
import Chip from '@material-ui/core/Chip';
import CheckedIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import { useDropzone } from 'react-dropzone';
import { StJwtLicenseInfo } from 'stserver-frontend-comp';
import { TabPanel } from './Components/TabPanel';
import QRCode from "react-qr-code";

const baseStyle = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    borderWidth: 2,
    borderRadius: 2,
    borderColor: '#eeeeee',
    borderStyle: 'dashed',
    backgroundColor: '#fafafa',
    color: '#bdbdbd',
    outline: 'none',
    transition: 'border .24s ease-in-out'
};

const activeStyle = {
    borderColor: '#2196f3'
};

const acceptStyle = {
    borderColor: '#00e676'
};

const rejectStyle = {
    borderColor: '#ff1744'
};




const Transition = forwardRef((props, ref) => {
    return <Slide direction="up" {...props} ref={ref} />;
})

const LicenseChip = props => {
    return (
        <Chip color='primary' style={(props.licenseInfo.valid) ? { backgroundColor: '#4caf50' } : { backgroundColor: '#f72500' }} icon={props.licenseInfo.valid ? <CheckedIcon /> : <ErrorIcon />} label={props.licenseInfo.message} variant="outlined" />
    )
}


const DropzoneLicense = props => {

    const onDrop = useCallback(acceptedFiles => {

        if (acceptedFiles && props.onFileDrop)
            props.onFileDrop(acceptedFiles);

    }, [props])

    const {
        getRootProps,
        getInputProps,
        isDragActive,
        isDragAccept,
        isDragReject
    } = useDropzone({ onDrop, multiple: true });

    const style = useMemo(() => ({
        ...baseStyle,
        ...(isDragActive ? activeStyle : {}),
        ...(isDragAccept ? acceptStyle : {}),
        ...(isDragReject ? rejectStyle : {}),
    }), [isDragActive, isDragReject, isDragAccept]);

    return (
        <div className="container">
            <div {...getRootProps({ style })}>
                <input {...getInputProps()} />
                <p>Drag 'n' drop a license file (.lic) here, or click to select it</p>
            </div>
        </div>
    )
}



function LicenseDlg(props) {
    const { server, dispatchServer } = useContext(serverContext);
    const { dispatchApp } = useContext(appContext);
    const themeCtx = useContext(themeContext);
    const [licenseInfo, setLicenseInfo] = useState(props.licenseInfo);
    const [value, setValue] = useState(0);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await fetchFromServer('licenseInfo', server);
                setLicenseInfo(res.data);
            } catch (error) {
                setLicenseInfo(null);
            }
        };

        if (server?.serverHost)
            fetchData();
    }, [server]);


    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const handleLicFileDrop = async files => {

        try {
            const res = await uploadToServer(`upload/license`, 'license', files, server);

            const validation = res.data;
            if (validation.valid)
                dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message: `License uploaded successfully.` } });
            else
                dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message: `Uploaded License is invalid`, severity: 'warning' } });

            const info = await fetchFromServer('licenseInfo', server);
            setLicenseInfo(info.data);

            dispatchServer({ type: 'UPDATE_LICENSE_VALID', payload: info.data });

        } catch (error) {

            dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message: `License upload error - ${error.response ? error.response.data : error.message}`, severity: 'error' } });
        }
    }


    return (
        <Dialog
            open={props.open}
            TransitionComponent={Transition}
            keepMounted
            onClose={() => props.onClose()}
        >
            <DialogTitle id="license-dialog-slide-title">
                {"🧾 License"}
            </DialogTitle>
            <DialogContent>
                <Tabs
                    value={value}
                    onChange={handleChange}
                    indicatorColor="primary"
                    textColor="primary"
                    centered
                >
                    <Tab label="NodeInfo" />
                    <Tab label="Upload" />
                    <Tab label="Options" />
                </Tabs>
                <TabPanel value={value} index={0} >
                    <div style={{ display: 'flex', justifyContent: 'center' }}>                     
                          <div style={{ height: "auto", marginTop: 10, maxWidth: 96, width: "100%" }}>
                            <QRCode
                                size={96}
                                style={{ width: '100%', height: 'auto' }}
                                value={server.nodeInfo}
                                viewBox="0 0 256 256"
                            />
                        </div>
                    </div>

                    <TextField
                        id="nodeinfo"
                        margin="dense"
                        value={server.nodeInfo}
                        fullWidth
                        InputProps={{
                            readOnly: true,
                            style: { fontSize: 14, textAlign: 'center' },
                            inputProps: {
                                style: { textAlign: 'center' }
                            }
                        }}
                    />
                    {
                        licenseInfo && <LicenseChip licenseInfo={licenseInfo} />
                    }
                </TabPanel>
                <TabPanel value={value} index={1} >
                    <DropzoneLicense onFileDrop={handleLicFileDrop} />
                </TabPanel>
                <TabPanel value={value} index={2} >
                    <StJwtLicenseInfo
                        serverUrl={server.serverHost}
                        username={server.username}
                        password={server.password}
                        theme={themeCtx.mode}
                    />
                </TabPanel>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => props.onClose()} color="primary">
                    Cancel
                </Button>
                <Button onClick={() => props.onClose()} color="primary">
                    Ok
                </Button>
            </DialogActions>
        </Dialog>
    );
}

LicenseDlg.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    licenseInfo: PropTypes.object
};

export default LicenseDlg;
